import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { addMonths, format, isAfter, isValid, parseISO } from "date-fns";
import { ReactNode, useState } from "react";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { useTeamsPickerTeamsQuery } from "../../../types";
import { UseAnalyticsFilters } from "../hooks/useAnalyticsFilters";
import { MAX_CUSTOM_RANGE_MONTHS, MenuAnalyticsMealPeriod, MenuAnalyticsTimeRange } from "../types";

export interface FilterBarProps extends UseAnalyticsFilters {
  rightSlot?: ReactNode;
}

const TIME_RANGE_OPTIONS: { value: MenuAnalyticsTimeRange; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "thisWeek", label: "This Week" },
  { value: "lastWeek", label: "Last Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "custom", label: "Custom" },
];

const MEAL_PERIOD_OPTIONS: { value: MenuAnalyticsMealPeriod; label: string }[] = [
  { value: "all", label: "All meals" },
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "snack", label: "Snack" },
];

const toIso = (d: Date) => format(d, "yyyy-MM-dd");

const validateRange = (start: Date | null, end: Date | null): string | null => {
  if (!start || !end || !isValid(start) || !isValid(end)) {
    return null;
  }
  const now = new Date();
  if (isAfter(start, now) || isAfter(end, now)) {
    return "Dates cannot be in the future.";
  }
  if (isAfter(start, end)) {
    return "End date must be on or after the start date.";
  }
  if (isAfter(end, addMonths(start, MAX_CUSTOM_RANGE_MONTHS))) {
    return `Date range cannot exceed ${MAX_CUSTOM_RANGE_MONTHS} months.`;
  }
  return null;
};

const SELECT_MENU_PROPS = { sx: { "& .MuiMenuItem-root": { fontSize: 14 } } };

const SELECT_VALUE_SX = { "& .MuiInputBase-input": { fontSize: 14 } };

export const FilterBar = ({ filters, setTimeRange, setCustomRange, setMealPeriod, setTeamId, rightSlot }: FilterBarProps) => {
  const { data: teamsData } = useTeamsPickerTeamsQuery();
  const teams = teamsData?.teams ?? [];
  const { formatDateWithLocale } = useDateFormatting();

  const [modalOpen, setModalOpen] = useState(false);
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const draftError = validateRange(draftStart, draftEnd);
  const canSave = !!draftStart && !!draftEnd && isValid(draftStart) && isValid(draftEnd) && !draftError;

  const openCustomModal = () => {
    setDraftStart(filters.startDate ? parseISO(filters.startDate) : null);
    setDraftEnd(filters.endDate ? parseISO(filters.endDate) : null);
    setModalOpen(true);
  };

  const handleSave = () => {
    if (draftStart && draftEnd && canSave) {
      setCustomRange(toIso(draftStart), toIso(draftEnd));
      setModalOpen(false);
    }
  };

  const renderTimeRangeValue = (value: string) => {
    if (value === "custom" && filters.startDate && filters.endDate) {
      return `${formatDateWithLocale(filters.startDate)} - ${formatDateWithLocale(filters.endDate)}`;
    }
    return TIME_RANGE_OPTIONS.find(o => o.value === value)?.label ?? "";
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 }}>
      <TextField
        select
        label="Time Range"
        size="medium"
        sx={{ minWidth: 180, ...SELECT_VALUE_SX }}
        value={filters.timeRange}
        onChange={e => {
          const value = e.target.value as MenuAnalyticsTimeRange;
          if (value !== "custom") {
            setTimeRange(value);
          }
        }}
        SelectProps={{ renderValue: v => renderTimeRangeValue(v as string), MenuProps: SELECT_MENU_PROPS }}
      >
        {TIME_RANGE_OPTIONS.map(o => (
          <MenuItem
            key={o.value}
            value={o.value}
            onClick={o.value === "custom" ? openCustomModal : undefined}
          >
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Meal Type"
        size="medium"
        sx={{ minWidth: 150, ...SELECT_VALUE_SX }}
        value={filters.mealPeriod}
        onChange={e => setMealPeriod(e.target.value as MenuAnalyticsMealPeriod)}
        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
      >
        {MEAL_PERIOD_OPTIONS.map(o => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Team"
        size="medium"
        sx={{ minWidth: 150, ...SELECT_VALUE_SX }}
        value={filters.teamId ?? "all"}
        onChange={e => setTeamId(e.target.value === "all" ? undefined : e.target.value)}
        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
      >
        <MenuItem value="all">All teams</MenuItem>
        {teams.map(team => (
          <MenuItem key={team.id} value={team.id}>
            {team.name}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ flex: 1 }} />

      {rightSlot}

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          Set Custom Time Range
          <IconButton aria-label="close" size="small" onClick={() => setModalOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", pt: 3 }}>
          <DesktopDatePicker
            label="Start Date"
            value={draftStart}
            onChange={setDraftStart}
            disableFuture
            maxDate={draftEnd ?? undefined}
            slotProps={{ textField: { size: "small", fullWidth: true, error: !!draftError } }}
          />
          <DesktopDatePicker
            label="End Date"
            value={draftEnd}
            onChange={setDraftEnd}
            disableFuture
            minDate={draftStart ?? undefined}
            slotProps={{ textField: { size: "small", fullWidth: true, error: !!draftError, sx: { mt: 2, mb: 2 } } }}
          />
          {draftError && <FormHelperText error sx={{ mt: 1 }}>{draftError}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
